import { create } from "zustand";
import { immer } from "zustand/middleware/immer";

type Sheet = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

type PanelHandle = {
  isDragging: boolean;
  setDragging: (dragging: boolean) => void;
};

type Panel = {
  size: number;
  setSize: (size: number) => void;
  handle: PanelHandle;
};

type BuilderState = {
  frame: {
    ref: HTMLIFrameElement | null;
    setRef: (ref: HTMLIFrameElement | null) => void;
  };
  sheet: {
    left: Sheet;
    right: Sheet;
    setOpen: (side: "left" | "right", open: boolean) => void;
  };
  panel: {
    left: Panel;
    right: Panel;
  };
};

type BuilderActions = {
  toggle: (side: "left" | "right") => void;
};

export const useBuilderStore = create<BuilderState & BuilderActions>()(
  immer((set) => ({
    frame: {
      ref: null,
      setRef: (ref) => {
        set((state) => {
          // @ts-expect-error Unable to set ref type
          state.frame.ref = ref;
        });
      },
    },
    sheet: {
      left: {
        open: true, // Left sidebar is open by default
        setOpen: (open) => {
          set((state) => {
            state.sheet.left.open = open;
            // If opening left sidebar, close right sidebar
            if (open) {
              state.sheet.right.open = false;
            }
            // If closing left sidebar, open right sidebar
            else if (!state.sheet.right.open) {
              state.sheet.right.open = true;
            }
          });
        },
      },
      right: {
        open: false,
        setOpen: (open) => {
          set((state) => {
            state.sheet.right.open = open;
            // If opening right sidebar, close left sidebar
            if (open) {
              state.sheet.left.open = false;
            }
            // If closing right sidebar, open left sidebar
            else if (!state.sheet.left.open) {
              state.sheet.left.open = true;
            }
          });
        },
      },
      setOpen: (side, open) => {
        set((state) => {
          state.sheet[side].open = open;
          // If opening a sidebar, close the other one
          if (open) {
            state.sheet[side === "left" ? "right" : "left"].open = false;
          }
          // If closing a sidebar, ensure the other one is open
          else if (!state.sheet[side === "left" ? "right" : "left"].open) {
            state.sheet[side === "left" ? "right" : "left"].open = true;
          }
        });
      },
    },
    panel: {
      left: {
        size: 0,
        setSize: (size) => {
          set((state) => {
            state.panel.left.size = size;
          });
        },
        handle: {
          isDragging: false,
          setDragging: (dragging) => {
            set((state) => {
              state.panel.left.handle.isDragging = dragging;
            });
          },
        },
      },
      right: {
        size: 0,
        setSize: (size) => {
          set((state) => {
            state.panel.right.size = size;
          });
        },
        handle: {
          isDragging: false,
          setDragging: (dragging) => {
            set((state) => {
              state.panel.right.handle.isDragging = dragging;
            });
          },
        },
      },
    },
    toggle: (side) => {
      set((state) => {
        const newState = !state.sheet[side].open;
        state.sheet[side].open = newState;
        // If opening a sidebar, close the other one
        if (newState) {
          state.sheet[side === "left" ? "right" : "left"].open = false;
        }
        // If closing a sidebar, ensure the other one is open
        else if (!state.sheet[side === "left" ? "right" : "left"].open) {
          state.sheet[side === "left" ? "right" : "left"].open = true;
        }
      });
    },
  })),
);
